import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import {
  LayoutDashboard,
  Target,
  ShieldAlert,
  Gamepad2,
  UserCircle,
  Menu,
  X,
  ChevronRight,
  Network,
  Compass,
  Activity,
  Calendar,
  FileText,
  CheckSquare,
  KanbanSquare,
  Search,
  MessageSquare,
  FolderOpen,
  Rocket,
  Map,
  BarChart3,
  Filter as Funnel,
  Home,
  Shield,
  Boxes,
  CalendarClock,
  ShoppingCart,
  BookOpen,
  Hammer,
  LogOut,
  Sun,
  Moon } from
  'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { motion, AnimatePresence } from 'framer-motion';
import LighthouseLoginModal from '@/components/lighthouse/LighthouseLoginModal';
import SqlChatAssistant from '@/components/chat/SqlChatAssistant';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import PullToRefresh from '@/components/common/PullToRefresh';
import AppVersionChangelog from '@/components/version/AppVersionChangelog';

export default function Layout({ children }) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [userRole, setUserRole] = React.useState(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [showLighthouseModal, setShowLighthouseModal] = React.useState(false);
  const [profile, setProfile] = React.useState(null);
  const [showSqlChat, setShowSqlChat] = React.useState(false);
  const [showMicroservices, setShowMicroservices] = React.useState(false);
  const [showLogoutMenu, setShowLogoutMenu] = React.useState(false);
  const microservicesTimerRef = React.useRef(null);

  /* ── Version + Changelog ── */
  const [currentVersion, setCurrentVersion] = React.useState(null);
  const [showChangelog, setShowChangelog] = React.useState(false);
  /* ─────────────────────── */

  /* ── Theme: Day / Night ── */
  const [isDarkMode, setIsDarkMode] = React.useState(() => {
    const saved = localStorage.getItem('compass-theme');
    return saved ? saved === 'dark' : true; /* default to dark */
  });

  React.useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('compass-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);
  /* ─────────────────────── */

  const openMicroservices = () => {
    clearTimeout(microservicesTimerRef.current);
    setShowMicroservices(true);
    microservicesTimerRef.current = setTimeout(() => setShowMicroservices(false), 5000);
  };

  const keepMicroservicesOpen = () => {
    clearTimeout(microservicesTimerRef.current);
  };

  const startMicroservicesClose = () => {
    microservicesTimerRef.current = setTimeout(() => setShowMicroservices(false), 400);
  };

  React.useEffect(() => {
    return () => clearTimeout(microservicesTimerRef.current);
  }, []);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on navigation
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const user = await base44.auth.me();
        setIsAdmin(user?.role === 'admin');

        const profiles = await base44.entities.OfficerProfile.list();
        const profile = profiles.find((p) => p.user_email === user?.email);
        setUserRole(profile?.custom_role);
        setProfile(profile);
      } catch (error) {
        console.error('Failed to fetch user role:', error);
      }
    };
    fetchUserRole();
  }, []);

  /* ── Fetch latest published AppVersion for sidebar badge ── */
  React.useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        const raw = await base44.entities.AppVersion.filter({ published: true }, '-release_date', 1);
        let data = raw;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { data = []; }
        }
        const arr = Array.isArray(data) ? data : [];
        if (arr.length > 0) setCurrentVersion(arr[0]);
      } catch (e) {
        /* AppVersion entity may not exist yet — silently skip */
        console.warn('Could not load AppVersion:', e?.message);
      }
    };
    fetchLatestVersion();
  }, []);
  /* ───────────────────────────────────────────────────────── */

  const handleLighthouseClick = (e) => {
    e.preventDefault();
    const dontShow = localStorage.getItem('dontShowLighthousePopup') === 'true';

    if (dontShow) {
      navigate(createPageUrl('lighthouseviewer'));
    } else {
      setShowLighthouseModal(true);
    }
  };

  const handleLighthouseProceed = (dontShowAgain) => {
    setShowLighthouseModal(false);
    navigate(createPageUrl('lighthouseviewer'));

    if (!dontShowAgain) {
      window.open('https://lighthouse.reefos.ai', '_blank');
    }
  };

  const handleLogout = async () => {
    try {
      await base44.auth.signOut();
    } catch (e) {
      console.error('Logout error:', e);
    }
    window.location.href = '/login';
  };

  const allNavItems = [
  { name: 'Welcome', icon: Home, path: '/welcome', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] },
  { name: 'The Framework', icon: Target, path: '/framework', roles: ['Commander in Chief', 'Commander', 'Analyst'] },
  { name: 'The Codex', icon: ShieldAlert, path: '/codex', roles: ['Commander in Chief', 'Commander'] },
  { name: 'Org Chart', icon: Network, path: '/orgchart', roles: ['Commander in Chief', 'Commander', 'Senior Officer'] },
  { name: 'Mission Control', icon: Rocket, path: '/missions', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] },
  { name: 'Objective Discovery', icon: Compass, path: '/discovery', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst'] },
  { name: 'Activities', icon: Activity, path: '/activities', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] },
  { name: 'Daily Tasks', icon: CheckSquare, path: '/dailytasks', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] },
  { name: 'Task Board', icon: KanbanSquare, path: '/taskboard', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] },
  { name: 'Artifacts & Rituals', icon: Calendar, path: '/artifacts', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] },
  { name: 'Issues, Policies & Capabilities', icon: FileText, path: '/issues', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] },
  { name: 'High Council', icon: Shield, path: '/highcouncil', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer'] },
  { name: 'Document Library', icon: FolderOpen, path: '/documentlibrary', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] }];


  const architectureNavItem = { name: 'Overall Architecture', icon: Search, path: '/overallarchitecture', roles: ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator'] };
  const feedbackNavItem = { name: 'Feedback', icon: MessageSquare, path: '/feedback', roles: ['Commander in Chief', 'Commander'] };
  const armoryNavItem = { name: 'Armory', icon: Hammer, path: '/armory', roles: ['Commander in Chief'] };

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => {
    if (isAdmin) return true; // Admins (Commanders) see everything
    if (!userRole) return true; // Show all if role not loaded yet
    return item.roles.includes(userRole);
  });
  const isCivMap = location.pathname === '/civilizationmap';

  /* ── Theme toggle button (reusable) ── */
  const ThemeToggleButton = () => (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle-btn"
          className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 transition-colors"
          style={{ lineHeight: 0 }}
        >
          {isDarkMode
            ? <Sun className="w-4 h-4 text-amber-400" />
            : <Moon className="w-4 h-4 text-indigo-400" />}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{isDarkMode ? 'Day view' : 'Night view'}</p>
      </TooltipContent>
    </Tooltip>
  );
  /* ──────────────────────────────────── */

  if (isCivMap) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        {/* Fixed Notification Bell - Top Right */}
        <div className="fixed top-4 right-6 z-50 flex items-center gap-1">
          <FeedbackButton />
          <NotificationCenter />
        </div>
        
        {children}
        <LighthouseLoginModal
          open={showLighthouseModal}
          onProceed={handleLighthouseProceed}
          onOpenChange={setShowLighthouseModal} />
        <SqlChatAssistant
          open={showSqlChat}
          onOpenChange={setShowSqlChat} />
        <AppVersionChangelog
          open={showChangelog}
          onOpenChange={setShowChangelog}
          isAdmin={isAdmin} />
      </div>
    );
  }

  return (
    <TooltipProvider>
    <PullToRefresh>
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Mobile Header Bar */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 h-14 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800 z-50 flex items-center justify-between px-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-md hover:bg-slate-800 text-slate-400 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-cyan-400" />
            <span className="font-bold text-sm tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
              COMPASS
            </span>
            {/* Theme toggle — mobile header, right of logo */}
            <ThemeToggleButton />
          </div>
          <div className="flex items-center gap-1">
            <FeedbackButton />
            <NotificationCenter />
          </div>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          width: isMobile ? 260 : (isSidebarOpen ? 260 : 80),
          x: isMobile ? (mobileMenuOpen ? 0 : -260) : 0,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`fixed left-0 top-0 h-screen bg-slate-900/50 backdrop-blur-xl border-r border-slate-800 flex flex-col ${isMobile ? 'z-50' : 'z-50'}`}
        style={isMobile ? { top: 0 } : undefined}>

        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="relative">
              {isSidebarOpen ?
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => setShowLogoutMenu(!showLogoutMenu)}>

                  <Compass className="w-6 h-20 text-cyan-400" />
                  <span className="font-bold text-xl tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
                    COMPASS
                  </span>
                </motion.div> :

              <Compass className="w-8 h-8 text-cyan-400 mx-auto cursor-pointer" onClick={() => setShowLogoutMenu(!showLogoutMenu)} />
              }

              {showLogoutMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowLogoutMenu(false)} />
                  <div className="absolute left-0 top-full mt-2 z-50 bg-slate-900/95 backdrop-blur-md border border-slate-700 rounded-lg shadow-xl py-1 min-w-[160px]">
                    <button
                      onClick={() => { setShowLogoutMenu(false); handleLogout(); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors">
                      <LogOut className="w-4 h-4" />
                      Log out
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Theme toggle — desktop sidebar, right of logo area */}
            <div className="flex items-center gap-1">
              <ThemeToggleButton />
              {!isMobile && (
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="p-1 rounded-md hover:bg-slate-800 text-slate-400 transition-colors absolute right-[-12px] top-8 bg-slate-900 border border-slate-700">
                  {isSidebarOpen ? <ChevronRight className="w-4 h-4 rotate-180" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>

          <TooltipProvider>
            <div className="flex flex-col items-center gap-2">
              {/* Row 1 */}
              <div className={`flex justify-center ${isSidebarOpen ? 'flex-row items-center gap-3' : 'flex-col items-center gap-2'}`}>
                {/* Avatar Circle */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to={createPageUrl('profile')}>
                      <div className={`relative rounded-full overflow-hidden border-2 border-cyan-400 cursor-pointer group ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        {profile?.avatar_url ?
                        <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" /> :

                        <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
                            <UserCircle className="w-8 h-8 text-white" />
                          </div>
                        }
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Search className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Officer Profile</p>
                  </TooltipContent>
                </Tooltip>

                {/* Game Controller Circle */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to={createPageUrl('')}>
                      <div className={`rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center cursor-pointer hover:from-indigo-500 hover:to-purple-500 transition-all border-2 border-indigo-400 ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        <Gamepad2 className="w-6 h-6 text-white" />
                      </div>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Command Deck</p>
                  </TooltipContent>
                </Tooltip>

                {/* Lighthouse Circle */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div data-testid="app-layout-clickable-div" onClick={handleLighthouseClick}>
                      <div className={`rounded-full overflow-hidden border-2 border-amber-400 cursor-pointer hover:border-amber-300 transition-all ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        <img
                          src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/a734e5_LightHouseIcon.jpg"
                          alt="Lighthouse"
                          className="w-full h-full object-cover" />

                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Lighthouse</p>
                  </TooltipContent>
                </Tooltip>

                {/* HexDB Circle */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div data-testid="app-layout-clickable-div-1" onClick={() => setShowSqlChat(true)}>
                      <div className={`rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center cursor-pointer hover:from-emerald-500 hover:to-teal-500 transition-all border-2 border-emerald-400 ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        <MessageSquare className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>HexDB</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Row 2 */}
                                  <div className={`flex justify-center ${isSidebarOpen ? 'flex-row items-center gap-3' : 'flex-col items-center gap-2'}`}>
                                    {/* Hexagon Analytics Circle */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <a href="https://ops-dashboard.base44.app" target="_blank" rel="noopener noreferrer">
                                          <div className={`rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center cursor-pointer hover:from-violet-500 hover:to-fuchsia-500 transition-all border-2 border-violet-400 ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                                            <BarChart3 className="w-6 h-6 text-white" />
                                          </div>
                                        </a>
                                      </TooltipTrigger>
                                      <TooltipContent side="right">
                                        <p>Hexagon Analytics</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Map View Circle */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Link to={createPageUrl('civilizationmap')}>
                                          <div className={`rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center cursor-pointer hover:from-amber-400 hover:to-yellow-500 transition-all border-2 border-amber-300 ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                                            <Map className="w-6 h-6 text-white" />
                                          </div>
                                        </Link>
                                      </TooltipTrigger>
                                      <TooltipContent side="right">
                                        <p>Map View</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Operator Funnel Circle */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <a
                                          href="https://dashboard.hexagon.reefos.ai/dashboard/41-operator-funnel?candidate=&city=&msa=&operator_id=&operator_name=&source=&stage=Accepted&stage=Active&stage=Applied&stage=Eligible&stage=Graduated&stage=Qualifying&sub-type=&tab=77-summary-table"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <div className={`rounded-full bg-gradient-to-br from-sky-600 to-cyan-600 flex items-center justify-center cursor-pointer hover:from-sky-500 hover:to-cyan-500 transition-all border-2 border-sky-400 ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                                            <Funnel className="w-6 h-6 text-white" />
                                          </div>
                                        </a>
                                      </TooltipTrigger>
                                      <TooltipContent side="right">
                                        <p>Operator Funnel</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Microservices Circle */}
                                    <div className="relative">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div data-testid="app-layout-clickable-div-2" onClick={() => showMicroservices ? setShowMicroservices(false) : openMicroservices()}>
                                            <div className={`rounded-full bg-gradient-to-br from-rose-600 to-pink-600 flex items-center justify-center cursor-pointer hover:from-rose-500 hover:to-pink-500 transition-all border-2 border-rose-400 ${isSidebarOpen ? 'w-12 h-12' : 'w-10 h-10'}`}>
                                              <Boxes className="w-6 h-6 text-white" />
                                            </div>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                          <p>Microservices</p>
                                        </TooltipContent>
                                      </Tooltip>
                                      <AnimatePresence>
                                        {showMicroservices && (
                                          <motion.div
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.8 }}
                                            transition={{ duration: 0.15 }}
                                            className="absolute left-1/2 -translate-x-1/2 top-full mt-2 flex gap-2 z-50 bg-slate-900/90 backdrop-blur-md rounded-2xl p-2 border border-slate-700 shadow-xl"
                                            onMouseEnter={keepMicroservicesOpen}
                                            onMouseLeave={startMicroservicesClose}
                                          >
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <a href="https://form-based-scheduler.base44.app/" target="_blank" rel="noopener noreferrer">
                                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-600 to-pink-600 flex items-center justify-center cursor-pointer hover:from-rose-500 hover:to-pink-500 transition-all border-2 border-rose-400">
                                                    <CalendarClock className="w-5 h-5 text-white" />
                                                  </div>
                                                </a>
                                              </TooltipTrigger>
                                              <TooltipContent side="bottom"><p>Scheduler</p></TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <a href="https://master-purchaser.base44.app" target="_blank" rel="noopener noreferrer">
                                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-600 to-pink-600 flex items-center justify-center cursor-pointer hover:from-rose-500 hover:to-pink-500 transition-all border-2 border-rose-400">
                                                    <ShoppingCart className="w-5 h-5 text-white" />
                                                  </div>
                                                </a>
                                              </TooltipTrigger>
                                              <TooltipContent side="bottom"><p>Purchaser</p></TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <a href="https://requirements-hub.base44.app/" target="_blank" rel="noopener noreferrer">
                                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-600 to-pink-600 flex items-center justify-center cursor-pointer hover:from-rose-500 hover:to-pink-500 transition-all border-2 border-rose-400">
                                                    <BookOpen className="w-5 h-5 text-white" />
                                                  </div>
                                                </a>
                                              </TooltipTrigger>
                                              <TooltipContent side="bottom"><p>SOPs</p></TooltipContent>
                                            </Tooltip>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  </div>
            </div>
          </TooltipProvider>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-2 flex flex-col overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700">
          <div className="space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || item.path !== '/' && location.pathname.startsWith(item.path);
              return (
                <Link key={item.path} to={createPageUrl(item.path.replace('/', ''))}>
                  <div className={`
                    flex items-center px-3 py-3 rounded-lg transition-all duration-200 group relative overflow-hidden
                    ${isActive ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'}
                  `}>
                    {isActive &&
                    <motion.div
                      layoutId="activeNav"
                      className="absolute inset-0 bg-indigo-500/10 rounded-lg"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }} />

                    }
                    <item.icon className={`w-5 h-5 ${isSidebarOpen ? 'mr-3' : 'mx-auto'} z-10`} />
                    {isSidebarOpen &&
                    <span className="font-medium text-sm z-10">{item.name}</span>
                    }
                  </div>
                </Link>);

            })}
          </div>

          {/* Spacer to push architecture link to bottom */}
          <div className="flex-1" />

          {/* Architecture Link - Separated at bottom */}
          <div className="pt-4 border-t border-slate-800/50 space-y-2">
            <Link to={createPageUrl(architectureNavItem.path.replace('/', ''))}>
                <div className={`
                  flex items-center px-3 py-3 rounded-lg transition-all duration-200 group relative overflow-hidden
                  ${location.pathname === architectureNavItem.path ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'}
                `}>
                  {location.pathname === architectureNavItem.path &&
                <motion.div
                  layoutId="activeNav"
                  className="absolute inset-0 bg-indigo-500/10 rounded-lg"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }} />

                }
                  <architectureNavItem.icon className={`w-5 h-5 ${isSidebarOpen ? 'mr-3' : 'mx-auto'} z-10`} />
                  {isSidebarOpen &&
                <span className="font-medium text-sm z-10">{architectureNavItem.name}</span>
                }
                </div>
              </Link>
            {isAdmin && (
              <Link to={createPageUrl(feedbackNavItem.path.replace('/', ''))}>
                <div className={`
                  flex items-center px-3 py-3 rounded-lg transition-all duration-200 group relative overflow-hidden
                  ${location.pathname === feedbackNavItem.path ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'}
                `}>
                  {location.pathname === feedbackNavItem.path &&
                <motion.div
                  layoutId="activeNavFeedback"
                  className="absolute inset-0 bg-indigo-500/10 rounded-lg"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }} />
                }
                  <feedbackNavItem.icon className={`w-5 h-5 ${isSidebarOpen ? 'mr-3' : 'mx-auto'} z-10`} />
                  {isSidebarOpen &&
                <span className="font-medium text-sm z-10">{feedbackNavItem.name}</span>
                }
                </div>
              </Link>
            )}
            {isAdmin && (
              <Link to={createPageUrl(armoryNavItem.path.replace('/', ''))}>
                <div className={`
                  flex items-center px-3 py-3 rounded-lg transition-all duration-200 group relative overflow-hidden
                  ${location.pathname === armoryNavItem.path ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'}
                `}>
                  {location.pathname === armoryNavItem.path &&
                <motion.div
                  layoutId="activeNavArmory"
                  className="absolute inset-0 bg-amber-500/10 rounded-lg"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }} />
                }
                  <armoryNavItem.icon className={`w-5 h-5 ${isSidebarOpen ? 'mr-3' : 'mx-auto'} z-10`} />
                  {isSidebarOpen &&
                <span className="font-medium text-sm z-10">{armoryNavItem.name}</span>
                }
                </div>
              </Link>
            )}
          </div>
        </nav>

        {/* ── Version badge — clickable, opens changelog ── */}
        <div className="p-4 border-t border-slate-800">
          {isSidebarOpen &&
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="version-badge-btn"
                onClick={() => setShowChangelog(true)}
                className="w-full text-xs text-slate-500 text-center hover:text-cyan-400 transition-colors cursor-pointer"
              >
                v {currentVersion?.version_string || '2.0.7'}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>What's new</p>
            </TooltipContent>
          </Tooltip>
          }
        </div>
        {/* ─────────────────────────────────────────────── */}

      </motion.aside>

      {/* Main Content */}
      <motion.main
        initial={false}
        animate={{ marginLeft: isMobile ? 0 : (isSidebarOpen ? 260 : 80) }}
        className={`min-h-screen relative ${isMobile ? 'pt-14' : ''}`}>

        {/* Fixed Notification Bell - Top Right (desktop only) */}
        {!isMobile && (
          <div className="fixed top-4 right-6 z-50 flex items-center gap-1">
            <FeedbackButton />
            <NotificationCenter />
          </div>
        )}

        <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/20 via-slate-950/0 to-slate-950/0" />
        <div className={`bg-slate-950 mx-auto relative z-10 max-w-7xl ${isMobile ? 'p-4' : 'p-8'}`}>
          {children}
        </div>
      </motion.main>

      <LighthouseLoginModal
        open={showLighthouseModal}
        onProceed={handleLighthouseProceed}
        onOpenChange={setShowLighthouseModal} />


      <SqlChatAssistant
        open={showSqlChat}
        onOpenChange={setShowSqlChat} />

      {/* ── Changelog Drawer ── */}
      <AppVersionChangelog
        open={showChangelog}
        onOpenChange={setShowChangelog}
        isAdmin={isAdmin} />
      {/* ───────────────────── */}

      </div>
    </PullToRefresh>
    </TooltipProvider>); 

}
